import type { FormEventHandler } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { UseMutationResult } from "@tanstack/react-query";
import { CreateAssignmentInput } from "@/lib/rpc";
import { CreateAssignmentData } from "@/lib/assignment";
import { AuditPlan } from "@/lib/audit-plan";
import { BusinessUnitPicker } from "@/components/business-unit-picker";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mutation: UseMutationResult<
    CreateAssignmentData,
    Error,
    CreateAssignmentInput
  >;
  auditPlan: AuditPlan;
};

const assingmentTypes = {
  regulatory_compliance: "Regulatory Compliance",
  financial_audit: "Financial Audit",
  operational_review: "Operational Review",
  it_security_assessment: "IT Security Assessment",
};

export const AddAssignmentDialog = ({
  open,
  onOpenChange,
  mutation,
  auditPlan,
}: Props) => {
  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const data = new FormData(event.target as HTMLFormElement);
    const userInput = Object.fromEntries(data);
    mutation.mutate({
      ...userInput,
      auditPlanId: auditPlan.id,
    } as CreateAssignmentInput);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a new Assignment</DialogTitle>
          <DialogDescription>
            Create a new assignment for the current Audit Plan
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-3 items-center gap-4">
              <Label htmlFor="name">Assignment Name</Label>
              <Input
                name="name"
                className="col-span-2"
                required
                autoComplete="false"
              />
            </div>
            <div className="grid grid-cols-3 items-center gap-4">
              <Label htmlFor="description">Description</Label>
              <Input
                name="description"
                className="col-span-2"
                placeholder="Brief description of the assignment..."
              />
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <Label htmlFor="type">Assignment type</Label>
              <Select name="type">
                <SelectTrigger className="col-span-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(assingmentTypes).map(([key, val]) => (
                    <SelectItem key={key} value={key}>
                      {val}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 items-center gap-4">
              <Label htmlFor="businessUnitId">Business Unit</Label>
              <BusinessUnitPicker
                className="col-span-2"
                name="businessUnitId"
                id="businessUnitId"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex gap-2 items-center">
                <Label htmlFor="startDate">Start</Label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  className="h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 col-span-2"
                />
              </div>

              <div className="flex gap-2 items-center">
                <Label htmlFor="endDate">End</Label>
                <input
                  id="endDate"
                  type="date"
                  name="endDate"
                  className="h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 col-span-2"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Create Assignment</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
